import { Users, FileText, DollarSign, CheckCircle, Calendar, Clock } from "lucide-react";
import SalepageRevenueChart from "../../../components/SalepageRevenueChart";

interface SalepageOverviewProps {
  stats: any;
  recentUsers: any[];
  recentPayments: any[];
  formatCurrency: (amount: number) => string;
  getStatusBadge: (status: string) => JSX.Element;
  onUserClick: (user: any) => void;
  navigate: (path: string) => void;
}

export default function SalepageOverview({ 
  stats, 
  recentUsers, 
  recentPayments, 
  formatCurrency, 
  getStatusBadge,
  onUserClick,
  navigate 
}: SalepageOverviewProps) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs text-green-600 font-semibold">+{stats.newUsersThisMonth} เดือนนี้</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.totalUsers}</h3>
          <p className="text-sm text-gray-500 mt-1">ผู้ใช้งานทั้งหมด</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.activeSubscriptions}</h3>
          <p className="text-sm text-gray-500 mt-1">Subscription ที่ใช้งาน</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.totalSalepages}</h3>
          <p className="text-sm text-gray-500 mt-1">Salepage ทั้งหมด</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <span className={`text-xs font-semibold ${stats.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.growthRate >= 0 ? '+' : ''}{stats.growthRate.toFixed(1)}%
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</h3>
          <p className="text-sm text-gray-500 mt-1">รายได้ทั้งหมด</p>
        </div>
      </div>

      <div className="mb-8">
        <SalepageRevenueChart />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            ผู้ใช้งานล่าสุด
          </h2>
          <div className="space-y-4">
            {recentUsers.length > 0 ? (
              recentUsers.map((user, index) => (
                <div 
                  key={index} 
                  onClick={() => onUserClick(user)}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{user.displayName || user.email}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    <p className="text-xs text-emerald-600 font-medium mt-1">
                      {user.salePageSubscription?.status === 'trial' ? 'ทดลองใช้' : 'Active'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">ยังไม่มีผู้ใช้งาน</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            การชำระเงินล่าสุด
          </h2>
          <div className="space-y-4">
            {recentPayments.length > 0 ? (
              recentPayments.map((payment, index) => (
                <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{formatCurrency(payment.amount)}</p>
                    <p className="text-xs text-gray-500">
                      {payment.createdAt?.toDate().toLocaleDateString('th-TH')}
                    </p>
                  </div>
                  {getStatusBadge(payment.status)}
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">ยังไม่มีการชำระเงิน</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
